/**
 * DiscoveryConfigService - Service for managing discovery sequences
 * Interfaces with backend API and static configuration
 */

import discoveryTopicsConfig from '../config/discoveryTopics.json';

class DiscoveryConfigService {
  constructor() {
    this.config = discoveryTopicsConfig;
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
  }

  // Static configuration methods
  
  /**
   * Get all available topics from static configuration
   */
  getAvailableTopics() {
    return this.config.topics;
  }

  /**
   * Get topic by key
   */
  getTopic(topicKey) {
    return this.config.topics[topicKey];
  }

  /**
   * Get all categories
   */
  getCategories() {
    return this.config.categories;
  }

  /**
   * Get template types
   */
  getTemplateTypes() {
    return this.config.templateTypes;
  }

  /**
   * Get default sequence for role type
   */
  getDefaultSequenceForRoleType(roleType) {
    const topicKeys = this.config.roleTypeDefaults[roleType] || [];
    return {
      name: `Default ${roleType} Sequence`,
      description: `Default discovery sequence for ${roleType} roles`,
      role_type: roleType,
      topics: topicKeys.map((topicKey, index) => {
        const topic = this.config.topics[topicKey];
        return {
          topic_key: topicKey,
          order_index: index,
          is_enabled: topic?.defaultEnabled || true,
          items: topic?.items ? Object.keys(topic.items).map(itemKey => ({
            item_key: itemKey,
            is_enabled: topic.items[itemKey].defaultEnabled || true,
            custom_config: {}
          })) : []
        };
      })
    };
  }

  // Backend API methods

  /**
   * Get all sequences for a user
   */
  async getUserSequences(userId) {
    try {
      const response = await fetch(`${this.baseUrl}/discovery-sequences/${userId}`);
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch user sequences');
      }

      return data.sequences || [];
    } catch (error) {
      console.error('Error fetching user sequences:', error);
      throw error;
    }
  }

  /**
   * Create a new sequence
   */
  async createSequence(sequenceData) {
    try {
      const response = await fetch(`${this.baseUrl}/discovery-sequences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sequenceData)
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to create sequence');
      }

      return data;
    } catch (error) {
      console.error('Error creating sequence:', error);
      throw error;
    }
  }

  /**
   * Update a sequence
   */
  async updateSequence(sequenceId, sequenceData) {
    try {
      const response = await fetch(`${this.baseUrl}/discovery-sequences/${sequenceId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(sequenceData)
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to update sequence');
      }

      return data;
    } catch (error) {
      console.error('Error updating sequence:', error);
      throw error;
    }
  }

  /**
   * Delete a sequence
   */
  async deleteSequence(sequenceId) {
    try {
      const response = await fetch(`${this.baseUrl}/discovery-sequences/${sequenceId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to delete sequence');
      }

      return data;
    } catch (error) {
      console.error('Error deleting sequence:', error);
      throw error;
    }
  }

  /**
   * Clone a sequence
   */
  async cloneSequence(sequenceId, newName, userId) {
    try {
      const response = await fetch(`${this.baseUrl}/discovery-sequences/${sequenceId}/clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: newName,
          user_id: userId
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to clone sequence');
      }

      return data;
    } catch (error) {
      console.error('Error cloning sequence:', error);
      throw error;
    }
  }

  /**
   * Set sequence as default
   */
  async setDefaultSequence(sequenceId) {
    try {
      const response = await fetch(`${this.baseUrl}/discovery-sequences/${sequenceId}/set-default`, {
        method: 'PUT'
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to set default sequence');
      }

      return data;
    } catch (error) {
      console.error('Error setting default sequence:', error);
      throw error;
    }
  }

  // Hybrid methods combining static config and user data

  /**
   * Get effective sequence for a user and role type
   * Returns user's custom sequence if exists, otherwise default from config
   */
  async getEffectiveSequence(userId, roleType) {
    try {
      const userSequences = await this.getUserSequences(userId);
      
      // Find user's default sequence for this role type
      const userDefault = userSequences.find(seq => 
        seq.role_type === roleType && seq.is_default === 1
      );

      if (userDefault) {
        return this.hydrateSequenceWithConfig(userDefault);
      }

      // Fall back to static default
      return this.getDefaultSequenceForRoleType(roleType);
    } catch (error) {
      console.error('Error getting effective sequence:', error);
      // Fall back to static default on error
      return this.getDefaultSequenceForRoleType(roleType);
    }
  }

  /**
   * Hydrate sequence data with static configuration
   * Merges user sequence with topic/item metadata from config
   */
  hydrateSequenceWithConfig(userSequence) {
    const hydratedSequence = {
      ...userSequence,
      topics: userSequence.topics?.map(userTopic => {
        const topicConfig = this.config.topics[userTopic.topic_key];
        return {
          ...userTopic,
          config: topicConfig,
          items: userTopic.items?.map(userItem => {
            const itemConfig = topicConfig?.items?.[userItem.item_key];
            return {
              ...userItem,
              config: itemConfig
            };
          }) || []
        };
      }) || []
    };

    return hydratedSequence;
  }

  /**
   * Create sequence from role type default
   * Useful for creating a user's first custom sequence
   */
  async createSequenceFromDefault(userId, roleType, customName) {
    const defaultSequence = this.getDefaultSequenceForRoleType(roleType);
    
    const sequenceData = {
      user_id: userId,
      name: customName || `My ${roleType} Sequence`,
      description: `Custom discovery sequence for ${roleType} roles`,
      role_type: roleType,
      is_default: true,
      topics: defaultSequence.topics
    };

    return await this.createSequence(sequenceData);
  }

  /**
   * Validate sequence data against static configuration
   */
  validateSequence(sequenceData) {
    const errors = [];

    if (!sequenceData.name || sequenceData.name.trim() === '') {
      errors.push('Sequence name is required');
    }

    if (!sequenceData.role_type) {
      errors.push('Role type is required');
    }

    if (!sequenceData.topics || !Array.isArray(sequenceData.topics)) {
      errors.push('Topics array is required');
    } else {
      sequenceData.topics.forEach((topic, topicIndex) => {
        if (!topic.topic_key) {
          errors.push(`Topic at index ${topicIndex} missing topic_key`);
        } else if (!this.config.topics[topic.topic_key]) {
          errors.push(`Invalid topic_key: ${topic.topic_key}`);
        }

        if (topic.items && Array.isArray(topic.items)) {
          const topicConfig = this.config.topics[topic.topic_key];
          topic.items.forEach((item, itemIndex) => {
            if (!item.item_key) {
              errors.push(`Item at topic ${topicIndex}, item ${itemIndex} missing item_key`);
            } else if (!topicConfig?.items?.[item.item_key]) {
              errors.push(`Invalid item_key: ${item.item_key} for topic: ${topic.topic_key}`);
            }
          });
        }
      });
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Get topics grouped by category
   */
  getTopicsByCategory() {
    const categorizedTopics = {};
    
    Object.keys(this.config.categories).forEach(categoryKey => {
      categorizedTopics[categoryKey] = {
        ...this.config.categories[categoryKey],
        topics: []
      };
    });

    Object.entries(this.config.topics).forEach(([topicKey, topic]) => {
      const categoryKey = topic.category;
      if (categorizedTopics[categoryKey]) {
        categorizedTopics[categoryKey].topics.push({
          key: topicKey,
          ...topic
        });
      }
    });

    return categorizedTopics;
  }

  /**
   * Get available role types
   */
  getAvailableRoleTypes() {
    return Object.keys(this.config.roleTypeDefaults);
  }
}

// Export singleton instance
const discoveryConfigService = new DiscoveryConfigService();
export default discoveryConfigService;